import { VIcon, VTextField, VSpacer, VListItem, VList, VSheet, VMenu } from 'vuetify/lib';
import { Metadata, getCountries, parsePhoneNumber, AsYouType } from 'libphonenumber-js/max';

var metadata = (new Metadata).metadata;
var intl = new Intl.DisplayNames(['en'], { type: 'region' });

/** @var {any} blankPhoneNumber */
var blankPhoneNumber = Object.freeze({
  number: '',
  nationalNumber: '',
  isValid: function () { return false; },
  isPossible: function () { return false; },
  getType: function () { return undefined; },
});

/** @var {any} validCountryCodesMap Country code-keyed object of truthy values */
var validCountryCodesMap = Object.freeze(getCountries().reduce(function (map, key) {
  map[key] = 1;
  return map;
}, {}));

/** @var {Array<{name: string, flag: string, countryCode: string, callingCode: string}>} countryCodeSelectorList Data for the country code selector */
var countryCodeSelectorList = Object.freeze(getCountries().map(function (key) { return ({
  name: intl.of(key),
  flag: getFlagEmoji(key),
  countryCode: key,
  callingCode: metadata.countries[key][0],
}); }));

/**
 * Get the [ISO 3166](https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes)
 * country code from a PhoneNumber object
 * @param {PhoneNumber} phoneNumber 
 * @returns string
 */
function getCountryCode(phoneNumber) {
  return phoneNumber.country
    ? phoneNumber.country
    : countryCodeSelectorList.find(function (item) { return item.callingCode === phoneNumber.countryCallingCode; }).countryCode;
}

/**
 * Get the national flag emoji of a country by its 2-digit
 * [ISO 3166](https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes)
 * country code
 * @param {string} countryCode 
 * @returns string
 */
function getFlagEmoji(countryCode) {
  return String.fromCodePoint.apply(String, countryCode.toUpperCase().split('').map(function (char) { return 127397 + char.charCodeAt(); }));
}

var script$1 = {
  components: {
    VIcon: VIcon,
    VTextField: VTextField,
    VSpacer: VSpacer,
    VListItem: VListItem,
    VList: VList,
    VSheet: VSheet,
    VMenu: VMenu
  },

  inheritAttrs: false,

  props: {
    // bound model
    value: {
      type: String,
      required: true,
    },
    // behavior
    disabled: {
      type: Boolean,
      default: function () { return false; },
    },
    readonly: {
      type: Boolean,
      default: function () { return false; },
    },
    // data settings
    defaultCountry: {
      type: String,
      default: function () { return 'US'; },
    },
    includedCountries: Array,
    excludedCountries: Array,
    // display settings
    color: String,
    success: {
      type: Boolean,
      default: function () { return false; },
    },
    hideFlag: {
      type: Boolean,
      default: function () { return false; },
    },
    hideCallingCode: {
      type: Boolean,
      default: function () { return false; },
    },
    showCountryCode: {
      type: Boolean,
      default: function () { return false; },
    },
    prependInnerIcon: {
      type: String,
      required: false,
    },
  },

  data: function () { return ({
    search: '',
    selection: {},
    menuOpen: false,
  }); },

  computed: {
    list: function list () {
      var this$1$1 = this;

      // included countries takes precedent
      if (this.includedCountries && this.includedCountries.length) {
        var map = this.includedCountries.reduce(function (obj, code) {
          obj[code] = code;
          return obj;
        }, {});
        return countryCodeSelectorList.filter(function (item) { return !! map[item.countryCode] && this$1$1.filterList(item); });
      }
      // excluded countries
      else if (this.excludedCountries && this.excludedCountries.length) {
        var map$1 = this.excludedCountries.reduce(function (obj, code) {
          obj[code] = code;
          return obj;
        }, {});
        return countryCodeSelectorList.filter(function (item) { return ! map$1[item.countryCode] && this$1$1.filterList(item); });
      }
      // whole list
      return countryCodeSelectorList.filter(function (item) { return this$1$1.filterList(item); });
    },
    displayText: function displayText () {
      var ret = '';
      if (! this.hideFlag) {
        ret += this.selection.flag + ' ';
      }
      if (this.showCountryCode) {
        ret += this.selection.countryCode + ' ';
      }
      if (! this.hideCallingCode) {
        ret += '+ ' + this.selection.callingCode;
      }
      return ret;
    },
    appendIconColor: function appendIconColor () {
      return this.disabled ? 'grey lighten-1' : null;
    },
  },

  watch: {
    value: function value (v) {
      this.selection = countryCodeSelectorList.find(function (item) { return item.countryCode === v; });
    },
    menuOpen: function menuOpen (v) {
      if (v)
        { this.search = ''; }
    },
  },

  methods: {
    filterList: function filterList (item) {
      return this.search
        ? item.name.concat(item.countryCode, item.callingCode).toLowerCase().includes(this.search.toLowerCase())
        : true;
    },
    select: function select (item) {
      this.$emit('input', item.countryCode);
      this.$emit('select');
    },
  },

  mounted: function mounted () {
    var this$1$1 = this;

    this.selection = countryCodeSelectorList.find(function (item) {
      if (this$1$1.value)
        { return item.countryCode === this$1$1.value; }
      return item.countryCode === this$1$1.defaultCountry;
    });
    this.$emit('input', this.selection.countryCode);
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    var options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    var hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            var originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            var existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

var isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return function (id, style) { return addStyle(id, style); };
}
var HEAD;
var styles = {};
function addStyle(id, css) {
    var group = isOldIE ? css.media || 'default' : id;
    var style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        var code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                { style.element.setAttribute('media', css.media); }
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            var index = style.ids.size - 1;
            var textNode = document.createTextNode(code);
            var nodes = style.element.childNodes;
            if (nodes[index])
                { style.element.removeChild(nodes[index]); }
            if (nodes.length)
                { style.element.insertBefore(textNode, nodes[index]); }
            else
                { style.element.appendChild(textNode); }
        }
    }
}

/* script */
var __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "v-phone-number__country-code" },
    [
      _c(
        "v-menu",
        {
          attrs: {
            "offset-y": "",
            "nudge-bottom": "4px",
            "nudge-left": "2px",
            disabled: _vm.disabled || _vm.readonly,
          },
          scopedSlots: _vm._u([
            {
              key: "activator",
              fn: function (ref) {
                var on = ref.on;
                return [
                  _c(
                    "div",
                    _vm._g(
                      {
                        staticClass:
                          "v-phone-number__country-code-display d-flex",
                        class: { active: _vm.menuOpen },
                      },
                      on
                    ),
                    [
                      _vm.prependInnerIcon
                        ? _c(
                            "div",
                            {
                              staticClass: "v-input__prepend-inner",
                              on: {
                                "click:prepend-inner": function (e) {
                                  return _vm.$emit("click:prepend-inner", e)
                                },
                              },
                            },
                            [
                              _c(
                                "div",
                                {
                                  staticClass:
                                    "v-input__icon v-input__icon--prepend",
                                },
                                [
                                  _c("v-icon", [
                                    _vm._v(_vm._s(_vm.prependInnerIcon)) ]) ],
                                1
                              ) ]
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _c("span", { staticClass: "mt-1" }, [
                        _vm._v(_vm._s(_vm.displayText)) ]),
                      _vm._v(" "),
                      !_vm.readonly
                        ? _c("div", { staticClass: "v-input__append-inner" }, [
                            _c(
                              "div",
                              {
                                staticClass:
                                  "v-input__icon v-input__icon--append",
                              },
                              [
                                _c(
                                  "v-icon",
                                  { attrs: { color: _vm.appendIconColor } },
                                  [_vm._v("mdi-menu-down")]
                                ) ],
                              1
                            ) ])
                        : _vm._e() ]
                  ) ]
              },
            } ]),
          model: {
            value: _vm.menuOpen,
            callback: function ($$v) {
              _vm.menuOpen = $$v;
            },
            expression: "menuOpen",
          },
        },
        [
          _vm._v(" "),
          _c(
            "v-sheet",
            { staticClass: "mt-n1" },
            [
              _c(
                "div",
                { staticClass: "v-phone-number__country-code-search px-3" },
                [
                  _c("v-text-field", {
                    attrs: {
                      autofocus: "",
                      "hide-details": "",
                      color: _vm.color,
                      success: _vm.success,
                      placeholder: "Search",
                    },
                    model: {
                      value: _vm.search,
                      callback: function ($$v) {
                        _vm.search = $$v;
                      },
                      expression: "search",
                    },
                  }) ],
                1
              ),
              _vm._v(" "),
              _c(
                "v-list",
                {
                  staticClass: "v-phone-number__country-list overflow-y-auto",
                  attrs: { dense: "", "max-height": "300px" },
                },
                _vm._l(_vm.list, function (item) {
                  return _c(
                    "v-list-item",
                    {
                      key: item.countryCode,
                      staticClass: "d-flex align-center",
                      on: {
                        click: function ($event) {
                          return _vm.select(item)
                        },
                      },
                    },
                    [
                      _c("span", { staticClass: "mr-2" }, [
                        _vm._v(_vm._s(item.flag)) ]),
                      _vm._v(" "),
                      _c("span", { staticClass: "mr-2" }, [
                        _vm._v(_vm._s(item.name)) ]),
                      _vm._v(" "),
                      _c("v-spacer"),
                      _vm._v(" "),
                      _c("span", [_vm._v("+" + _vm._s(item.callingCode))]) ],
                    1
                  )
                }),
                1
              ) ],
            1
          ) ],
        1
      ) ],
    1
  )
};
var __vue_staticRenderFns__$1 = [];
__vue_render__$1._withStripped = true;

  /* style */
  var __vue_inject_styles__$1 = function (inject) {
    if (!inject) { return }
    inject("data-v-6596b614_0", { source: ".v-phone-number .v-input--dense:not(.v-text-field--enclosed) .v-phone-number__country-code {\n  margin-top: 2px;\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line) .v-phone-number__country-code {\n  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused .v-phone-number__country-code {\n  transform: translateY(9px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-input--dense .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-input--dense .v-phone-number__country-code {\n  transform: translateY(12px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-text-field--outlined .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-text-field--outlined .v-phone-number__country-code {\n  transform: translateY(10px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-text-field--outlined.v-input--dense .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-text-field--outlined.v-input--dense .v-phone-number__country-code {\n  transform: translateY(0px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-input--dense, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-input--dense {\n  transform: none;\n}\n.v-phone-number .v-input:not(.v-input--is-disabled).theme--light .v-phone-number__country-code-display {\n  color: #000000;\n}\n.v-phone-number .v-input:not(.v-input--is-disabled).theme--dark .v-phone-number__country-code-display {\n  color: #FFFFFF;\n}\n.v-phone-number .v-phone-number__country-code-display {\n  cursor: pointer;\n  white-space: nowrap;\n}\n.v-phone-number .v-phone-number__country-code-display .v-input__prepend-inner,\n.v-phone-number .v-phone-number__country-code-display .v-input__append-inner {\n  margin-top: 0 !important;\n}\n.v-phone-number .v-phone-number__country-code-display .v-input__icon--append {\n  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), visibility 0s;\n}\n.v-phone-number .v-phone-number__country-code-display.active .v-input__icon--append {\n  transform: rotate(180deg);\n}\n.v-phone-number .v-input--is-readonly .v-phone-number__country-code-display {\n  cursor: unset;\n}\n.v-phone-number .v-input--is-disabled .v-phone-number__country-code-display,\n.v-phone-number .v-input--is-disabled .v-input__prepend-inner {\n  cursor: default;\n}\n.v-phone-number .v-phone-number__country-list {\n  scrollbar-width: none;\n  -ms-overflow-style: none;\n}\n.v-phone-number .v-phone-number__country-list::-webkit-scrollbar {\n  background: transparent;\n  width: 0;\n}\n\n/*# sourceMappingURL=CountrySelector.vue.map */", map: {"version":3,"sources":["/Users/mikedoughty/Code/vuetify-phone-number/src/CountrySelector.vue","CountrySelector.vue"],"names":[],"mappings":"AA+LA;EACA,eAAA;AC9LA;ADoMA;EACA,gDAAA;AClMA;ADsMA;EACA,0BAAA;ACpMA;ADuMA;EACA,2BAAA;ACrMA;ADyMA;EACA,2BAAA;ACvMA;AD0MA;EACA,0BAAA;ACxMA;AD4MA;EACA,eAAA;AC1MA;ADgNA;EACA,cAAA;AC9MA;ADmNA;EACA,cAAA;ACjNA;ADqNA;EACA,eAAA;EACA,mBAAA;ACnNA;ADqNA;;EAEA,wBAAA;ACnNA;ADsNA;EACA,+DAAA;ACpNA;ADuNA;EACA,yBAAA;ACrNA;ADyNA;EACA,aAAA;ACvNA;AD0NA;;EAEA,eAAA;ACxNA;AD2NA;EACA,qBAAA;EACA,wBAAA;ACzNA;AD2NA;EACA,uBAAA;EACA,QAAA;ACzNA;;AAEA,8CAA8C","file":"CountrySelector.vue","sourcesContent":["<template>\n  <div class=\"v-phone-number__country-code\">\n    <v-menu\n        offset-y\n        nudge-bottom=\"4px\"\n        nudge-left=\"2px\"\n        :disabled=\"disabled || readonly\"\n        v-model=\"menuOpen\">\n      <template #activator=\"{ on }\">\n        <div\n            class=\"v-phone-number__country-code-display d-flex\"\n            :class=\"{ 'active': menuOpen }\"\n            v-on=\"on\">\n          <div\n              class=\"v-input__prepend-inner\"\n              v-if=\"prependInnerIcon\" \n              @click:prepend-inner=\"(e) => $emit('click:prepend-inner', e)\">\n            <div class=\"v-input__icon v-input__icon--prepend\">\n              <v-icon>{{ prependInnerIcon }}</v-icon>\n            </div>\n          </div>\n          <span class=\"mt-1\">{{ displayText }}</span>\n          <div v-if=\"! readonly\" class=\"v-input__append-inner\">\n            <div class=\"v-input__icon v-input__icon--append\">\n              <v-icon :color=\"appendIconColor\">mdi-menu-down</v-icon>\n            </div>\n          </div>\n        </div>\n      </template>\n      <v-sheet class=\"mt-n1\">\n        <div class=\"v-phone-number__country-code-search px-3\">\n          <v-text-field\n              autofocus hide-details\n              :color=\"color\"\n              :success=\"success\"\n              placeholder=\"Search\"\n              v-model=\"search\" />\n        </div>\n        <v-list dense max-height=\"300px\" class=\"v-phone-number__country-list overflow-y-auto\">\n          <v-list-item\n              class=\"d-flex align-center\"\n              v-for=\"item in list\"\n              :key=\"item.countryCode\"\n              @click=\"select(item)\">\n            <span class=\"mr-2\">{{ item.flag }}</span>\n            <span class=\"mr-2\">{{ item.name }}</span>\n            <v-spacer />\n            <span>+{{ item.callingCode }}</span>\n          </v-list-item>\n        </v-list>\n      </v-sheet>\n    </v-menu>\n  </div>\n</template>\n\n<script>\nimport { countryCodeSelectorList } from './lib/core';\n\nexport default {\n  inheritAttrs: false,\n\n  props: {\n    // bound model\n    value: {\n      type: String,\n      required: true,\n    },\n    // behavior\n    disabled: {\n      type: Boolean,\n      default: () => false,\n    },\n    readonly: {\n      type: Boolean,\n      default: () => false,\n    },\n    // data settings\n    defaultCountry: {\n      type: String,\n      default: () => 'US',\n    },\n    includedCountries: Array,\n    excludedCountries: Array,\n    // display settings\n    color: String,\n    success: {\n      type: Boolean,\n      default: () => false,\n    },\n    hideFlag: {\n      type: Boolean,\n      default: () => false,\n    },\n    hideCallingCode: {\n      type: Boolean,\n      default: () => false,\n    },\n    showCountryCode: {\n      type: Boolean,\n      default: () => false,\n    },\n    prependInnerIcon: {\n      type: String,\n      required: false,\n    },\n  },\n\n  data: () => ({\n    search: '',\n    selection: {},\n    menuOpen: false,\n  }),\n\n  computed: {\n    list () {\n      // included countries takes precedent\n      if (this.includedCountries && this.includedCountries.length) {\n        const map = this.includedCountries.reduce((obj, code) => {\n          obj[code] = code;\n          return obj;\n        }, {});\n        return countryCodeSelectorList.filter(item =>  !! map[item.countryCode] && this.filterList(item));\n      }\n      // excluded countries\n      else if (this.excludedCountries && this.excludedCountries.length) {\n        const map = this.excludedCountries.reduce((obj, code) => {\n          obj[code] = code;\n          return obj;\n        }, {});\n        return countryCodeSelectorList.filter(item => ! map[item.countryCode] && this.filterList(item));\n      }\n      // whole list\n      return countryCodeSelectorList.filter(item => this.filterList(item));\n    },\n    displayText () {\n      let ret = '';\n      if (! this.hideFlag) {\n        ret += this.selection.flag + ' ';\n      }\n      if (this.showCountryCode) {\n        ret += this.selection.countryCode + ' ';\n      }\n      if (! this.hideCallingCode) {\n        ret += '+ ' + this.selection.callingCode;\n      }\n      return ret;\n    },\n    appendIconColor () {\n      return this.disabled ? 'grey lighten-1' : null;\n    },\n  },\n\n  watch: {\n    value (v) {\n      this.selection = countryCodeSelectorList.find(item => item.countryCode === v);\n    },\n    menuOpen (v) {\n      if (v)\n        this.search = '';\n    },\n  },\n\n  methods: {\n    filterList (item) {\n      return this.search\n        ? item.name.concat(item.countryCode, item.callingCode).toLowerCase().includes(this.search.toLowerCase())\n        : true;\n    },\n    select (item) {\n      this.$emit('input', item.countryCode);\n      this.$emit('select');\n    },\n  },\n\n  mounted () {\n    this.selection = countryCodeSelectorList.find(item => {\n      if (this.value)\n        return item.countryCode === this.value;\n      return item.countryCode === this.defaultCountry;\n    });\n    this.$emit('input', this.selection.countryCode);\n  },\n};\n</script>\n\n<style lang=\"scss\">\n@import '../node_modules/vuetify/src/styles/styles.sass';\n\n.v-phone-number {\n  // fix positioning in default dense inputs\n  .v-input--dense:not(.v-text-field--enclosed) {\n    .v-phone-number__country-code {\n      margin-top: 2px;\n    }\n  }\n\n  // need to handle the movement which occurs with labelled, filled inputs\n  .v-text-field--filled:not(.v-text-field--single-line) {\n    .v-phone-number__country-code {\n      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);\n    }\n    \n    &.v-input--is-dirty, &.v-input--is-focused {\n      .v-phone-number__country-code {\n        transform: translateY(9px);\n      }\n\n      &.v-input--dense .v-phone-number__country-code {\n        transform: translateY(12px);\n      }\n\n      // filled and outlined\n      &.v-text-field--outlined .v-phone-number__country-code {\n        transform: translateY(10px);\n      }\n\n      &.v-text-field--outlined.v-input--dense .v-phone-number__country-code {\n        transform: translateY(0px);\n      }\n\n      // I can't figure out a better way to stop filled, dense stuff from teleporting 12px\n      &.v-input--dense {\n        transform: none;\n      }\n    }\n  }\n\n  .v-input:not(.v-input--is-disabled).theme--light {\n    .v-phone-number__country-code-display {\n        color: map-get($material-light, 'text-color');\n    }\n  }\n\n  .v-input:not(.v-input--is-disabled).theme--dark {\n    .v-phone-number__country-code-display {\n        color: map-get($material-dark, 'text-color');\n    }\n  }\n\n  .v-phone-number__country-code-display {\n    cursor: pointer;\n    white-space: nowrap;\n\n    .v-input__prepend-inner,\n    .v-input__append-inner {\n      margin-top: 0 !important;\n    }\n\n    .v-input__icon--append {\n      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), visibility 0s;\n    }\n\n    &.active .v-input__icon--append {\n      transform: rotate(180deg);\n    }\n  }\n\n  .v-input--is-readonly .v-phone-number__country-code-display {\n    cursor: unset;\n  }\n\n  .v-input--is-disabled .v-phone-number__country-code-display,\n  .v-input--is-disabled .v-input__prepend-inner {\n    cursor: default;\n  }\n\n  .v-phone-number__country-list {\n    scrollbar-width: none;\n    -ms-overflow-style: none;\n\n    &::-webkit-scrollbar {\n      background: transparent;\n      width: 0;\n    }\n  }\n}\n</style>\n",".v-phone-number .v-input--dense:not(.v-text-field--enclosed) .v-phone-number__country-code {\n  margin-top: 2px;\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line) .v-phone-number__country-code {\n  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused .v-phone-number__country-code {\n  transform: translateY(9px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-input--dense .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-input--dense .v-phone-number__country-code {\n  transform: translateY(12px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-text-field--outlined .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-text-field--outlined .v-phone-number__country-code {\n  transform: translateY(10px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-text-field--outlined.v-input--dense .v-phone-number__country-code, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-text-field--outlined.v-input--dense .v-phone-number__country-code {\n  transform: translateY(0px);\n}\n.v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-dirty.v-input--dense, .v-phone-number .v-text-field--filled:not(.v-text-field--single-line).v-input--is-focused.v-input--dense {\n  transform: none;\n}\n.v-phone-number .v-input:not(.v-input--is-disabled).theme--light .v-phone-number__country-code-display {\n  color: #000000;\n}\n.v-phone-number .v-input:not(.v-input--is-disabled).theme--dark .v-phone-number__country-code-display {\n  color: #FFFFFF;\n}\n.v-phone-number .v-phone-number__country-code-display {\n  cursor: pointer;\n  white-space: nowrap;\n}\n.v-phone-number .v-phone-number__country-code-display .v-input__prepend-inner,\n.v-phone-number .v-phone-number__country-code-display .v-input__append-inner {\n  margin-top: 0 !important;\n}\n.v-phone-number .v-phone-number__country-code-display .v-input__icon--append {\n  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), visibility 0s;\n}\n.v-phone-number .v-phone-number__country-code-display.active .v-input__icon--append {\n  transform: rotate(180deg);\n}\n.v-phone-number .v-input--is-readonly .v-phone-number__country-code-display {\n  cursor: unset;\n}\n.v-phone-number .v-input--is-disabled .v-phone-number__country-code-display,\n.v-phone-number .v-input--is-disabled .v-input__prepend-inner {\n  cursor: default;\n}\n.v-phone-number .v-phone-number__country-list {\n  scrollbar-width: none;\n  -ms-overflow-style: none;\n}\n.v-phone-number .v-phone-number__country-list::-webkit-scrollbar {\n  background: transparent;\n  width: 0;\n}\n\n/*# sourceMappingURL=CountrySelector.vue.map */"]}, media: undefined });

  };
  /* scoped */
  var __vue_scope_id__$1 = undefined;
  /* module identifier */
  var __vue_module_identifier__$1 = undefined;
  /* functional template */
  var __vue_is_functional_template__$1 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var __vue_component__$1 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    createInjector,
    undefined,
    undefined
  );

var _ = {
  /**
   * Clone an object
   * @param {any} entity 
   * @returns any
   */
  cloneDeep: function cloneDeep(entity) {
    if (typeof entity === 'object') {
      var ret;
      if (Array.isArray(entity)) {
        ret = [];
      } else if (entity !== null) {
        ret = {};
      // return null
      } else {
        return entity;
      }
      // recurse!
      for (var key in entity) {
        ret[key] = this.cloneDeep(entity[key]);
      }
      return ret;
    } else {
      // return a primitive
      return entity;
    }
  },

  /**
   * Create a new object which only has the given properties
   * and methods of a given object
   * @param {any} entity 
   * @param {Array<string>} keys 
   * @returns any
   */
  pick: function pick(entity, keys) {
    var this$1$1 = this;

    return keys.reduce(function (ret, key) {
      if (Object.prototype.hasOwnProperty.call(entity, key)) {
        ret[key] = this$1$1.cloneDeep(entity[key]);
      }
      return ret;
    }, {});
  },

  /**
   * Create a new object which does not have the given
   * properties or methods from a given object
   * @param {any} entity 
   * @param {Array<string>} keys 
   * @returns any
   */
  omit: function omit(entity, keys) {
    var this$1$1 = this;

    return Object.keys(entity).reduce(function (ret, key) {
      if (! keys.includes(key)) {
        ret[key] = this$1$1.cloneDeep(entity[key]);
      }
      return ret;
    }, {});
  },
};

var script = {
  name: 'v-phone-number',
  inheritAttrs: false,

  components: {
    CountrySelector: __vue_component__$1,
    VTextField: VTextField
  },

  props: {
    // bound model
    value: {
      type: String,
      default: function () { return ''; },
    },
    // country code data settings
    defaultCountry: {
      type: String,
      default: function () { return 'US'; },
    },
    includedCountries: {
      type: Array,
      default: function () { return []; },
    },
    excludedCountries: {
      type: Array,
      default: function () { return []; },
    },
    // country code display settings
    disableCountrySelect: {
      type: Boolean,
      default: function () { return false; },
    },  
    hideFlag: {
      type: Boolean,
      default: function () { return false; },
    },
    hideCallingCode: {
      type: Boolean,
      default: function () { return false; },
    },
    showCountryCode: {
      type: Boolean,
      default: function () { return false; },
    },
    // validation settings
    voip: {
      type: Boolean,
      default: function () { return false; },
    },
    mobile: {
      type: Boolean,
      default: function () { return false; },
    },
    landline: {
      type: Boolean,
      default: function () { return false; },
    },
    strict: {
      type: Boolean,
      default: function () { return false; },
    },
    required: {
      type: Boolean,
      default: function () { return false; },
    },
  },

  data: function () { return ({
    lazyCountryCode: '',
    nationalNumber: '',
    errorMessage: '',
  }); },

  computed: {
    // passthroughs
    attrs: function attrs () {
      var keys = this.disableCountrySelect
        ? [ 'counter', 'counterValue', 'hideSpinButtons', 'type', 'rules' ]
        : [ 'counter', 'counterValue', 'hideSpinButtons', 'type', 'rules', 'prependInnerIcon' ];
      return _.omit(this.$attrs, keys);
    },
    listeners: function listeners () {
      var keys = this.disableCountrySelect
        ? [ 'blur', 'focus', 'input', 'keydown' ]
        : [ 'blur', 'focus', 'input', 'keydown', 'click:prepend-inner' ];
      return _.omit(this.$listeners, keys);
    },
    slots: function slots () {
      var keys = this.disableCountrySelect
        ? [ 'counter', 'progress' ]
        : [ 'counter', 'progress', 'prepend-inner' ];
      return _.omit(this.$slots, keys);
    },
    scopedSlots: function scopedSlots () {
      var keys = this.disableCountrySelect
        ? [ 'counter', 'progress' ]
        : [ 'counter', 'progress', 'prepend-inner' ];
      return _.omit(this.$scopedSlots, keys);
    },
    countrySelectorAttributes: function countrySelectorAttributes () {
      return Object.assign({}, {defaultCountry: this.defaultCountry,
        includedCountries: this.includedCountries.filter(function (country) { return !! validCountryCodesMap[country]; }),
        excludedCountries: this.excludedCountries.filter(function (country) { return !! validCountryCodesMap[country]; }),
        hideFlag: this.hideFlag,
        hideCallingCode: this.hideCallingCode,
        showCountryCode: this.showCountryCode},
        // pick vuetify options
        _.pick(this.$attrs, [ 'color', 'disabled', 'prependInnerIcon', 'readonly', 'success' ]));
    },
    // data
    countryCode: function countryCode () {
      return this.disableCountrySelect ? this.defaultCountry : this.lazyCountryCode;
    },
    phoneNumber: function phoneNumber () {
      try { return parsePhoneNumber(this.nationalNumber, this.countryCode); }
      catch (e) { return blankPhoneNumber; }
    },
    // validation
    numberIsValid: function numberIsValid () {
      return this.strict
        ? this.phoneNumber.isValid()
        : this.phoneNumber.isPossible();
    },
    numberIsVoip: function numberIsVoip () {
      return (this.phoneNumber.getType() || '').includes('VOIP');
    },
    numberIsMobile: function numberIsMobile () {
      return (this.phoneNumber.getType() || '').includes('MOBILE');
    },
    numberIsLandline: function numberIsLandline () {
      return (this.phoneNumber.getType() || '').includes('FIXED');
    },
    numberTypeRestrictionValid: function numberTypeRestrictionValid () {
      return [ this.voip, this.mobile, this.landline ]
        .filter(function (v) { return v; })
        .length <= 1;
    },
    rules: function rules () {
      var this$1$1 = this;

      var rules = [ function (v) { return ! v || this$1$1.numberIsValid || 'Not a phone number'; } ];
      if (this.required) {
        rules.unshift(function (v) { return !! v || 'Required'; });
      }
      // these types are mutually exclusive
      if (this.voip) {
        rules.push(function (v) { return ! v || this$1$1.numberIsVoip || 'Not a VOIP number'; });
      } else if (this.mobile) {
        rules.push(function (v) { return ! v || this$1$1.numberIsMobile || 'Not a mobile number'; });
      } else if (this.landline) {
        rules.push(function (v) { return ! v || this$1$1.numberIsLandline || 'Not a landline'; });
      }
      return rules;
    },
  },

  watch: {
    value: function value (value$1) {
      try {
        var phoneNumber = parsePhoneNumber(value$1);
        if (! phoneNumber.isEqual(this.phoneNumber)) {
          this.nationalNumber = phoneNumber.format('NATIONAL');
          this.lazyCountryCode = getCountryCode(phoneNumber);
        }
      }
      catch (e) {
        // don't care
      }
    },
    lazyCountryCode: function lazyCountryCode () {
      if (this.nationalNumber) {
        this.emit(this.nationalNumber);
      }
      if (this.$refs.number_input.$el.classList.value.includes('v-text-field--outlined')) {
        this.$nextTick(function () { return window.dispatchEvent(new Event('resize')); });
      }
    },
    numberTypeRestrictionValid: function numberTypeRestrictionValid (v) {
      if (! v) {
        console.warn('VPhoneNumber: The properties `voip`, `mobile`, and `landline` are mutally exclusive. Setting more than one of these to true will behave unpredictably.');
      }
    },
  },

  methods: {
    // method passthroughs
    blur: function blur () {
      var ref;

      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];
      return (ref = this.$refs.number_input).blur.apply(ref, args);
    },
    focus: function focus () {
      var ref;

      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];
      return (ref = this.$refs.number_input).focus.apply(ref, args);
    },
    validate: function validate () {
      var ref;

      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];
      return (ref = this.$refs.number_input).validate.apply(ref, args);
    },
    // event reponses
    onCountryCodeUpdate: function onCountryCodeUpdate (event) {
      this.$emit('update:country-code', event);
      this.$refs.number_input.focus();
    },
    onKeydown: function onKeydown (event) {
      this.$emit('keydown', event);
      var isInteger = /^[0-9]$/.test(event.key) && ! event.shiftKey;
      var isNav = /^((Arrow(Left|Up|Right|Down))|Home|End|Backspace|Delete|Tab|Enter)$/.test(event.key);
      var isFunction = /^F(1[012]|[1-9])$/.test(event.key);
      var isSuper = /((^(OS|Alt|Shift|Control))|Lock)$/.test(event.key);
      if (! (isInteger || isNav || isFunction || isSuper || event.ctrlKey || event.metaKey || event.key === '+')) {
        event.preventDefault();
      }
    },
    onInput: function onInput (input) {
      // skip if emptied
      if (! input) {
        this.$emit('input', input);
        this.$emit('phone-number', blankPhoneNumber);
        return;
      }
      try {
        var phoneNumber = parsePhoneNumber(input);
        if (phoneNumber && phoneNumber.country && phoneNumber.country !== this.countryCode) {
          this.lazyCountryCode = phoneNumber.country;
          input = input.replace(("+" + (phoneNumber.countryCallingCode)), '');
        }
      }
      catch (e) {
        // don't care if the parse fails
      }
      this.emit(input.replace(/[^-()0-9+]/g, ''));
    },
    onFocus: function onFocus (event) {
      this.$emit('focus', event);
      if (this.$refs.number_input.$el.classList.value.includes('v-text-field--outlined')) {
        window.dispatchEvent(new Event('resize'));
      }
    },
    onBlur: function onBlur (event) {
      this.$emit('blur', event);
      this.validate();
    },
    emit: function emit (value) {
      this.nationalNumber = (new AsYouType(this.countryCode)).input(value);
      this.$emit('input', this.phoneNumber.number);
      this.$emit('phone-number', this.phoneNumber);
      if (this.errorMessage) {
        this.$nextTick(this.onBlur);
      }
    },
  },

  mounted: function mounted () {
    try {
      var phoneNumber = parsePhoneNumber(this.value);
      this.nationalNumber = phoneNumber.nationalNumber;
      this.lazyCountryCode = getCountryCode(phoneNumber);
      if (this.$refs.number_input.$el.classList.value.includes('v-text-field--outlined')) {
        this.$nextTick(function () { return window.dispatchEvent(new Event('resize')); });
      }
    }
    catch (e) {
      // don't care
    }
  },
};

/* script */
var __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "v-phone-number" },
    [
      _c(
        "v-text-field",
        _vm._g(
          _vm._b(
            {
              ref: "number_input",
              staticClass: "v-phone-number__number-input",
              attrs: { value: _vm.nationalNumber, rules: _vm.rules },
              on: {
                blur: _vm.onBlur,
                focus: _vm.onFocus,
                input: _vm.onInput,
                keydown: _vm.onKeydown,
              },
              scopedSlots: _vm._u(
                [
                  !_vm.disableCountrySelect
                    ? {
                        key: "prepend-inner",
                        fn: function () {
                          return [
                            _c(
                              "country-selector",
                              _vm._b(
                                {
                                  on: {
                                    select: function () {
                                      return _vm.$refs.number_input.focus()
                                    },
                                    input: function (v) {
                                      return _vm.$emit("update:country-code", v)
                                    },
                                    "click:prepend-inner": function (e) {
                                      return _vm.$emit("click:prepend-inner", e)
                                    },
                                  },
                                  model: {
                                    value: _vm.lazyCountryCode,
                                    callback: function ($$v) {
                                      _vm.lazyCountryCode = $$v;
                                    },
                                    expression: "lazyCountryCode",
                                  },
                                },
                                "country-selector",
                                _vm.countrySelectorAttributes,
                                false
                              )
                            ) ]
                        },
                        proxy: true,
                      }
                    : null,
                  _vm._l(_vm.scopedSlots, function (_, name) {
                    return {
                      key: name,
                      fn: function (scope) {
                        return [_vm._t(name, null, null, scope)]
                      },
                    }
                  }) ],
                null,
                true
              ),
            },
            "v-text-field",
            _vm.attrs,
            false
          ),
          _vm.listeners
        ),
        [
          _vm._v(" "),
          _vm._l(_vm.slots, function (_, name) {
            return _vm._t(name, null, { slot: name })
          }) ],
        2
      ) ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  var __vue_inject_styles__ = undefined;
  /* scoped */
  var __vue_scope_id__ = undefined;
  /* module identifier */
  var __vue_module_identifier__ = undefined;
  /* functional template */
  var __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

/* eslint no-undef: 0 */

// Declare install function executed by Vue.use()
function install(Vue) {
  if (install.installed) { return; }
  install.installed = true;
  Vue.component('VPhoneNumber', __vue_component__);
}

// Create module definition for Vue.use()
var plugin = {
  install: install,
};

// Auto-install when vue is found (eg. in browser via <script> tag)
var GlobalVue = null;
if (window) {
  GlobalVue = window.Vue;
} else if (global) {
  GlobalVue = global.Vue;
}
if (GlobalVue) {
  GlobalVue.use(plugin);
}

export { __vue_component__ as default, install };
